import { Link, useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck, Building2, KeyRound, Plane, Hammer, CheckCircle2, Clock, Coins, ArrowLeft } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo } from "@/i18n";
import { SERVICES } from "@/data/services";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";

const ICONS = { ShieldCheck, Building2, KeyRound, Plane, Hammer };

export function Services() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="container-site py-28 md:py-36" data-testid="services-page">
      <SEO title={`${t.nav.services} — ${t.brand}`} description={t.home.servicesSub} lang={lang} pathKey="services" />
      <Reveal>
        <p className="eyebrow">{t.nav.services}</p>
        <h1 className="h2-serif mt-3">{t.home.servicesTitle}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.servicesSub}</p>
      </Reveal>
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.slug} delay={i * 0.06}>
              <Link to={linkTo(lang, "service", s.slug)} className="card-site p-8 flex gap-6 h-full group" data-testid={`service-overview-${s.slug}`}>
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#B5472B]/10 text-[#B5472B]"><Icon size={26} strokeWidth={1.6} /></span>
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-[#1F2328] group-hover:text-[#B5472B] transition-colors">{s[lang].name}</h2>
                  <p className="mt-2 text-sm text-[#1F2328]/65 leading-relaxed">{s[lang].short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0E4D5C]">{t.common.readMore} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-14 text-center">
        <Link to={linkTo(lang, "contact")} className="btn-primary" data-testid="services-cta">{t.nav.cta} <ArrowRight size={16} /></Link>
      </Reveal>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = T[lang];
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) {
    return (
      <div className="container-site py-40 text-center">
        <h1 className="h2-serif">{t.notFound.title}</h1>
        <Link to={linkTo(lang, "services")} className="btn-primary mt-8">{t.common.back}</Link>
      </div>
    );
  }
  const Icon = ICONS[s.icon];
  return (
    <div className="container-site py-28 md:py-36 max-w-4xl" data-testid="service-detail-page">
      <SEO title={`${s[lang].name} — ${t.brand}`} description={s[lang].short} lang={lang} pathKey="service" slug={s.slug} />
      <Link to={linkTo(lang, "services")} className="inline-flex items-center gap-2 text-sm text-[#0E4D5C] hover:text-[#B5472B]"><ArrowLeft size={15} /> {t.nav.services}</Link>
      <Reveal className="mt-6">
        <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#B5472B]/10 text-[#B5472B]"><Icon size={30} strokeWidth={1.5} /></span>
        <h1 className="h2-serif mt-5">{s[lang].name}</h1>
        <p className="mt-4 text-lg text-[#1F2328]/70 leading-relaxed">{s[lang].short}</p>
      </Reveal>
      <Reveal className="mt-12">
        <div className="rounded-2xl bg-[#B5472B]/5 border-l-4 border-[#B5472B] p-6">
          <p className="label-site !text-[#B5472B]">{lang === "it" ? "Il problema" : "The problem"}</p>
          <p className="text-[#1F2328]/80 leading-relaxed">{s[lang].problem}</p>
        </div>
      </Reveal>
      <Reveal className="mt-8">
        <h2 className="font-serif text-2xl text-[#1F2328]">{lang === "it" ? "Cosa facciamo" : "What we do"}</h2>
        <p className="mt-3 text-[#1F2328]/80 leading-relaxed">{s[lang].what}</p>
      </Reveal>
      <Reveal className="mt-8">
        <h2 className="font-serif text-2xl text-[#1F2328]">{lang === "it" ? "Cosa include" : "What's included"}</h2>
        <ul className="mt-4 space-y-3">
          {s[lang].includes.map((inc) => (
            <li key={inc} className="flex items-start gap-3 text-[#1F2328]/80"><CheckCircle2 size={17} className="text-[#2FA3A0] mt-1 shrink-0" /> {inc}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="card-site p-5 flex items-center gap-4"><Clock className="text-[#0E4D5C]" size={22} /><div><p className="label-site !mb-0">{lang === "it" ? "Tempi indicativi" : "Indicative timing"}</p><p className="font-medium">{s[lang].timing}</p></div></div>
        <div className="card-site p-5 flex items-center gap-4"><Coins className="text-[#0E4D5C]" size={22} /><div><p className="label-site !mb-0">{lang === "it" ? "Costo" : "Cost"}</p><p className="font-medium">{s[lang].price}</p></div></div>
      </Reveal>
      <Reveal className="mt-12 rounded-3xl bg-[#0E4D5C] text-[#F6EFE4] p-8 md:p-10 text-center">
        <h2 className="font-serif text-2xl sm:text-3xl">{t.home.ctaTitle}</h2>
        <Link to={linkTo(lang, "contact")} className="btn-primary mt-6" data-testid="service-detail-cta">{t.nav.cta} <ArrowRight size={16} /></Link>
      </Reveal>
    </div>
  );
}
